import React, { useState, useEffect } from "react";

// Import MUI elements
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import { Tooltip as ReactTooltip } from "react-tooltip";

function Product({
  parentToChild,
  onResetProduct,
  onSelectedProductChange,
  onSelectedReasonChange,
}: {
  parentToChild: any;
  onResetProduct: any;
  onSelectedProductChange: any;
  onSelectedReasonChange: any;
}) {
  // Get data from parent component
  const {
    startDate,
    endDate,
    selectedOption,
    selectedRadio,
    isBeingResetProduct,
    selectedCallCenter,
    mode,
  } = parentToChild;

  // State variables for PRODUCTS
  const [dataProdByVolume, setProdByVolume] = useState<any[]>([]);
  const [dataProdByReason, setProdByReason] = useState<any[]>([]);
  const [dataProdType, setProdType] = useState<string>("");
  const [dataReasonType, setReasonType] = useState<string>("");
  const [prodTypeLabel, setProdTypeLabel] = useState<string>("");
  const [reasonTypeLabel, setReasonTypeLabel] = useState<string>("");

  const [displayText, setDisplayText] = useState<string>("");
  const [showAll, setShowAll] = useState<boolean>(false);
  const visibleProducts = showAll
    ? dataProdByVolume
    : dataProdByVolume.slice(0, 3);

  // State variables for PROD CALL REASONS
  const [showAllTwo, setShowAllTwo] = useState<boolean>(false);
  const visibleProdReasons = showAllTwo
    ? dataProdByReason
    : dataProdByReason.slice(0, 3);

  // Skeleton element helper
  const [isProdLoading, setIsProdLoading] = useState<boolean>(true);
  const [isProdTypeLoading, setIsProdTypeLoading] = useState<boolean>(true);

  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState<string>("");

  //console.log(dataProdType);
  //console.log(dataReasonType);

  const handleProductSelection = (label: string) => {
    // Reset the reason type
    setSelectedReason("");
    onSelectedReasonChange("");
    setReasonType("");
    setReasonTypeLabel("");

    // Set the new product type
    setProdType(label);
    setSelectedProduct(label);
    onSelectedProductChange(label);
  };

  const handleReasonSelection = (label: string) => {
    // Reset the product type
    setSelectedProduct("");
    onSelectedProductChange("");
    setProdType("");
    setProdTypeLabel("All Products");

    // Set the new reason type
    setReasonType(label);
    setReasonTypeLabel(label);
    setSelectedReason(label);
    onSelectedReasonChange(label);
  };

  // Reset all useState variables when reset button is pressed in parent component
  useEffect(() => {
    if (isBeingResetProduct === true) {
      setProdByVolume([]);
      setProdByReason([]);
      setProdType("");
      setProdTypeLabel("");
      setDisplayText("");
      setShowAll(false);
      setShowAllTwo(false);
      setIsProdLoading(true);
      setIsProdTypeLoading(true);
      setSelectedProduct("");
      setSelectedReason("");
      setReasonType("");
      onResetProduct();
    }
  }, [isBeingResetProduct, onResetProduct]);

  // Get data for PRODUCTS
  useEffect(() => {
    setIsProdLoading(true);
    let apiEndpoint;
    let processData: (data: any) => any; // Explicitly declare the type of processData

    let formattedReasonType;

    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    let formattedCallCenter;
    if (selectedCallCenter === "All") {
      formattedCallCenter = "&call_center_id=";
    } else {
      formattedCallCenter = "&call_center_id=" + selectedCallCenter;
    }

    switch (selectedRadio) {
      case "total_call_volume":
        if (dataReasonType !== "") {
          setDisplayText("Call Volume");
          //setReasonTypeLabel(dataProdType);
          formattedReasonType = dataReasonType.replace(/ /g, "%20");
          apiEndpoint = `/prod_by_volume_by_callreason?callreason=${formattedReasonType}&`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                current_volume,
                percent_change,
                product_name,
              }: {
                current_volume: string;
                percent_change: string;
                product_name: string;
              }) => [product_name, percent_change, parseInt(current_volume, 10)]
            );
          };
        } else {
          setDisplayText("Call Volume");
          apiEndpoint = "/prod_by_volume?";
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                current_volume,
                percent_change,
                product_name,
              }: {
                current_volume: string;
                percent_change: string;
                product_name: string;
              }) => [product_name, percent_change, parseInt(current_volume, 10)]
            );
          };
        }
        break;
      case "average_handle_time":
        if (dataReasonType !== "") {
          setDisplayText("Average Handle Time");
          formattedReasonType = dataReasonType.replace(/ /g, "%20");
          apiEndpoint = `/product_by_average_handletime_by_callreason?callreason=${formattedReasonType}&`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_handletime,
                percent_change,
                product_name,
              }: {
                average_handletime: string;
                percent_change: string;
                product_name: string;
              }) => [product_name, percent_change, average_handletime]
            );
          };
        } else {
          setDisplayText("Average Handle Time");
          apiEndpoint = "/product_by_average_handletime?";
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_handletime,
                percent_change,
                product_name,
              }: {
                average_handletime: string;
                percent_change: string;
                product_name: string;
              }) => [product_name, percent_change, average_handletime]
            );
          };
        }
        break;
      case "average_wait_time":
        if (dataReasonType !== "") {
          setDisplayText("Average Wait Time");
          formattedReasonType = dataReasonType.replace(/ /g, "%20");
          apiEndpoint = `/product_by_average_waittime_by_callreason?callreason=${formattedReasonType}&`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_waittime,
                percent_change,
                product_name,
              }: {
                average_waittime: string;
                percent_change: string;
                product_name: string;
              }) => [
                product_name,
                percent_change,
                parseInt(average_waittime, 10),
              ]
            );
          };
        } else {
          setDisplayText("Average Wait Time");
          apiEndpoint = "/product_by_average_waittime?";
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_waittime,
                percent_change,
                product_name,
              }: {
                average_waittime: string;
                percent_change: string;
                product_name: string;
              }) => [
                product_name,
                percent_change,
                parseInt(average_waittime, 10),
              ]
            );
          };
        }
        break;
      case "resolution_rate":
        if (dataReasonType !== "") {
          setDisplayText("Resolution Rate");
          formattedReasonType = dataReasonType.replace(/ /g, "%20");
          apiEndpoint = `/product_by_resolution_rate_by_callreason?callreason=${formattedReasonType}&`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                resolution_rate,
                percent_change,
                product_name,
              }: {
                resolution_rate: string;
                percent_change: string;
                product_name: string;
              }) => [
                product_name,
                percent_change,
                parseInt(resolution_rate, 10),
              ]
            );
          };
        } else {
          setDisplayText("Resolution Rate");
          apiEndpoint = "/product_by_resolution_rate?";
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                resolution_rate,
                percent_change,
                product_name,
              }: {
                resolution_rate: string;
                percent_change: string;
                product_name: string;
              }) => [
                product_name,
                percent_change,
                parseInt(resolution_rate, 10),
              ]
            );
          };
        }
        break;
      default:
        apiEndpoint = "/prod_by_volume?";
        processData = (data) => {
          return [];
        };
    }

    fetch(
      `${apiEndpoint}start_date=${formattedStartDate}&end_date=${formattedEndDate}${formattedCallCenter}`
    )
      .then((res) => res.text())
      .then((data) => {
        const resultArray = processData(data);
        setProdByVolume(resultArray);
        setIsProdLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching ${selectedRadio} data:`, error);
        setIsProdLoading(false);
      });
  }, [startDate, endDate, selectedRadio, selectedCallCenter, dataReasonType]);
  //console.log(dataProdByVolume)

  // Get data for PROD CALL REASONS
  useEffect(() => {
    setIsProdTypeLoading(true);
    let apiEndpoint;
    let processData: (data: any) => any; // Explicitly declare the type of processData
    let formattedProdType;
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    let formattedCallCenter;
    if (selectedCallCenter === "All") {
      formattedCallCenter = "&call_center_id=";
    } else {
      formattedCallCenter = "&call_center_id=" + selectedCallCenter;
    }

    //console.log(dataProdType)

    switch (selectedRadio) {
      case "total_call_volume":
        if (dataProdType !== "") {
          setProdTypeLabel(dataProdType);
          formattedProdType = dataProdType.replace(/ /g, "%20");
          apiEndpoint = `/product_call_volume_by_call_reason?product_name=${formattedProdType}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                call_volume,
                percent_change,
                call_reason,
              }: {
                call_volume: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, parseInt(call_volume, 10)]
            );
          };
        } else {
          setProdTypeLabel("All Products");
          apiEndpoint = `/product_call_volume_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                call_volume,
                percent_change,
                call_reason,
              }: {
                call_volume: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, parseInt(call_volume, 10)]
            );
          };
        }
        break;
      case "average_handle_time":
        if (dataProdType !== "") {
          setProdTypeLabel(dataProdType);
          formattedProdType = dataProdType.replace(/ /g, "%20");
          apiEndpoint = `/product_average_handle_time_by_call_reason?product_name=${formattedProdType}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_handle_time,
                percent_change,
                call_reason,
              }: {
                average_handle_time: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, average_handle_time]
            );
          };
        } else {
          setProdTypeLabel("All Products");
          apiEndpoint = `/product_average_handle_time_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_handle_time,
                percent_change,
                call_reason,
              }: {
                average_handle_time: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, average_handle_time]
            );
          };
        }
        break;
      case "average_wait_time":
        if (dataProdType !== "") {
          setProdTypeLabel(dataProdType);
          formattedProdType = dataProdType.replace(/ /g, "%20");
          apiEndpoint = `/product_average_wait_time_by_call_reason?product_name=${formattedProdType}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_wait_time,
                percent_change,
                call_reason,
              }: {
                average_wait_time: string;
                percent_change: string;
                call_reason: string;
              }) => [
                call_reason,
                percent_change,
                parseInt(average_wait_time, 10),
              ]
            );
          };
        } else {
          setProdTypeLabel("All Products");
          apiEndpoint = `/product_average_wait_time_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                average_wait_time,
                percent_change,
                call_reason,
              }: {
                average_wait_time: string;
                percent_change: string;
                call_reason: string;
              }) => [
                call_reason,
                percent_change,
                parseInt(average_wait_time, 10),
              ]
            );
          };
        }
        break;
      case "resolution_rate":
        if (dataProdType !== "") {
          setProdTypeLabel(dataProdType);
          formattedProdType = dataProdType.replace(/ /g, "%20");
          apiEndpoint = `/product_resolution_rate_by_callreason?product_name=${formattedProdType}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                resolution_rate,
                percent_change,
                call_reason,
              }: {
                resolution_rate: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, parseInt(resolution_rate, 10)]
            );
          };
        } else {
          setProdTypeLabel("All Products");
          apiEndpoint = `/product_resolution_rate_by_callreason?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                resolution_rate,
                percent_change,
                call_reason,
              }: {
                resolution_rate: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, parseInt(resolution_rate, 10)]
            );
          };
        }
        break;
      default:
        if (dataProdType !== "") {
          setProdTypeLabel(dataProdType);
          formattedProdType = dataProdType.replace(/ /g, "%20");
          apiEndpoint = `/product_call_volume_by_call_reason?product_name=${formattedProdType}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                call_volume,
                percent_change,
                call_reason,
              }: {
                call_volume: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, parseInt(call_volume, 10)]
            );
          };
        } else {
          setProdTypeLabel("All Products");
          apiEndpoint = `/product_call_volume_by_call_reason?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
          processData = (data) => {
            const parsedData = JSON.parse(data);
            return parsedData.map(
              ({
                call_volume,
                percent_change,
                call_reason,
              }: {
                call_volume: string;
                percent_change: string;
                call_reason: string;
              }) => [call_reason, percent_change, parseInt(call_volume, 10)]
            );
          };
        }
    }

    fetch(`${apiEndpoint}${formattedCallCenter}`)
      .then((res) => res.text())
      .then((data) => {
        const resultArray = processData(data);
        setProdByReason(resultArray);
        setIsProdTypeLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching ${selectedRadio} data:`, error);
        setIsProdTypeLoading(false);
      });
  }, [
    selectedOption,
    selectedRadio,
    selectedCallCenter,
    dataProdType,
    startDate,
    endDate,
  ]);
  //console.log(dataProdByReason);

  return (
    <div>
      <Divider className='divider-2' />
      {isProdLoading ? (
        <div className='exec-prod-container'>
          <Skeleton height={200} width={"100%"} />
        </div>
      ) : (
        <div className='radio-form-container'>
          <h3 className='radio-form-label'>
            Product by {displayText}:{" "}
            {reasonTypeLabel === "" ? "All Reasons" : reasonTypeLabel}
            {displayText === "Resolution Rate" &&
              (mode === "dark" ? (
                <span className='shine'>
                  <AutoAwesomeIcon />
                </span>
              ) : (
                <span className='ai-driven'>
                  <AutoAwesomeIcon />
                </span>
              ))}
          </h3>
          <div className='exec-radio-form'>
            <div className='exec-summary-group'>
              <List>
                <div className='exec-list'>
                  {visibleProducts.map(([label, percent, value], index) => (
                    <ListItem key={index}>
                      <ListItemText
                        className={
                          selectedProduct === label
                            ? "selected"
                            : selectedProduct === ""
                            ? "default"
                            : "not-selected"
                        }
                        data-tooltip-id='exec-prod-tooltip'
                        style={{
                          cursor: "pointer",
                          color:
                            selectedProduct === label ? "#1976d2" : "black",
                        }}
                        onClick={() => handleProductSelection(label)}
                      >
                        {label}
                      </ListItemText>
                      <span className='float-right number'>
                        {selectedRadio === "total_call_volume"
                          ? Math.round(value)
                          : selectedRadio === "resolution_rate"
                          ? `${(Math.round(value * 10) / 10).toFixed(1)}%`
                          : selectedRadio === "average_wait_time"
                          ? `${Math.round(value)}sec`
                          : selectedRadio === "average_handle_time"
                          ? new Date(value * 60 * 1000)
                              .toISOString()
                              .substr(14, 5)
                          : value}
                      </span>
                      {selectedOption !== "Last 7 days" || percent === "N/A" ? (
                        <span className='bold-num'>N/A</span>
                      ) : (
                        <span className={`float-right`}>
                          <span
                            className={`${
                              parseFloat(percent) === 0
                                ? "no-arrow"
                                : (selectedRadio === "total_call_volume" &&
                                    (parseFloat(percent) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "resolution_rate" &&
                                    (parseFloat(percent) < 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_wait_time" &&
                                    (parseFloat(percent) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_handle_time" &&
                                    (parseFloat(percent) > 0
                                      ? "red-arrow"
                                      : "green-arrow"))
                            } number`}
                          >
                            {parseFloat(percent) === 0
                              ? "00.0%"
                              : (parseFloat(percent) < 0 ? "" : "") +
                                Math.abs(parseFloat(percent))
                                  .toFixed(1)
                                  .padStart(4, "0") +
                                "%"}
                            {parseFloat(percent) === 0
                              ? "⬅"
                              : parseFloat(percent) < 0
                              ? "⬇"
                              : "⬆"}
                          </span>
                        </span>
                      )}
                    </ListItem>
                  ))}
                </div>
                {dataProdByVolume.length > 3 && (
                  <Button size='medium' onClick={() => setShowAll(!showAll)}>
                    {showAll ? "Hide" : "Show All"}
                  </Button>
                )}
              </List>
            </div>
          </div>
        </div>
      )}
      <Divider className='divider-2' />
      {isProdTypeLoading ? (
        <div className='exec-prod-container'>
          <Skeleton height={200} width={"100%"} />
        </div>
      ) : (
        <div className='radio-form-container prod-reasons-section'>
          <h3 className='radio-form-label'>
            Call Reasons by {displayText}: {prodTypeLabel}
            {displayText === "Resolution Rate" &&
              (mode === "dark" ? (
                <span className='shine'>
                  <AutoAwesomeIcon />
                </span>
              ) : (
                <span className='ai-driven'>
                  <AutoAwesomeIcon />
                </span>
              ))}
          </h3>
          <div className='exec-radio-form'>
            <div className='exec-summary-group'>
              <List>
                <div className='exec-list'>
                  {visibleProdReasons.map(([label, percent, value], index) => (
                    <ListItem key={index}>
                      <ListItemText
                        className={
                          selectedReason === label
                            ? "selected"
                            : selectedReason === ""
                            ? "default"
                            : "not-selected"
                        }
                        data-tooltip-id='exec-prod-tooltip'
                        style={{
                          cursor: "pointer",
                          color: selectedReason === label ? "#1976d2" : "black",
                        }}
                        onClick={() => handleReasonSelection(label)}
                      >
                        {label}
                      </ListItemText>
                      <span className='float-right number'>
                        {selectedRadio === "total_call_volume"
                          ? Math.round(value)
                          : selectedRadio === "resolution_rate"
                          ? `${(Math.round(value * 10) / 10).toFixed(1)}%`
                          : selectedRadio === "average_wait_time"
                          ? value + "sec"
                          : selectedRadio === "average_handle_time"
                          ? new Date(value * 60 * 1000)
                              .toISOString()
                              .substr(14, 5)
                          : value}
                      </span>
                      {selectedOption !== "Last 7 days" || percent === "N/A" ? (
                        <span className='bold-num'>N/A</span>
                      ) : (
                        <span className={`float-right`}>
                          <span
                            className={`${
                              parseFloat(percent) === 0
                                ? "no-arrow"
                                : (selectedRadio === "total_call_volume" &&
                                    (parseFloat(percent) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "resolution_rate" &&
                                    (parseFloat(percent) < 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_wait_time" &&
                                    (parseFloat(percent) > 0
                                      ? "red-arrow"
                                      : "green-arrow")) ||
                                  (selectedRadio === "average_handle_time" &&
                                    (parseFloat(percent) > 0
                                      ? "red-arrow"
                                      : "green-arrow"))
                            } number`}
                          >
                            {parseFloat(percent) === 0
                              ? "00.0%"
                              : (parseFloat(percent) < 0 ? "" : "") +
                                Math.abs(parseFloat(percent))
                                  .toFixed(1)
                                  .padStart(4, "0") +
                                "%"}
                            {parseFloat(percent) === 0
                              ? "⬅"
                              : parseFloat(percent) < 0
                              ? "⬇"
                              : "⬆"}
                          </span>
                        </span>
                      )}
                    </ListItem>
                  ))}
                </div>

                {dataProdByReason.length > 3 && (
                  <Button
                    size='medium'
                    onClick={() => setShowAllTwo(!showAllTwo)}
                  >
                    {showAllTwo ? "Hide" : "Show All"}
                  </Button>
                )}
              </List>
            </div>
          </div>
        </div>
      )}
      <ReactTooltip
        id='exec-prod-tooltip'
        style={{
          borderRadius: "5px",
          ...(mode === "dark" && {
            backgroundColor: "#f5f5f5",
            color: "black",
          }),
        }}
        place='bottom'
        content='Select to visualize'
      />
    </div>
  );
}

export default Product;
